import React from 'react';
import styled from 'styled-components';
import { useAdminSystemAlert } from '../../share/hook/useAdminSystemAlert';
import SelectBox from '../../../shared/component/SelectBox';
import { SystemAlertType, SelectType, TextAlignment, AlertType, AlertPosition } from '../../../shared/enum/EnumRepository';
import HeadlessInput from '../../../shared/component/headless/input/HeadlessInput';
import { NormalInput } from '../../../shared/component/headless/input/InputBody';
import { BlackFitBtnHeartbeat } from '../../../shared/component/headless/button/BtnBody';
import { PaperBlurFitBtn } from '../../../shared/component/headless/button/BtnBody';
import HeadlessBtn from '../../../shared/component/headless/button/HeadlessBtn';
import { NormalTextArea } from '../../../shared/component/headless/textarea/TextAreaBody';
import OrgDescriptionAdmin from '../../../shared/component/organism/OrgDescriptionAdmin';
import { useStandardAlertStore } from '../../../shared/store/portal/alertStore';
import { localInputToIsoInstant } from '../../../shared/util/timeInvertor/time';
import { SYSTEM_ALERT_MESSAGE_MAX_LENGTH } from '../../../shared/constants/limits';
import { getAlertMessage } from '../../../shared/constants/alertMessages';

const SystemAlertViewer: React.FC = () => {
  const { activeSystemAlert, setActiveSystemAlert, createSystemAlert, endSystemAlert, deleteSystemAlert } = useAdminSystemAlert();
  const { enqueueAlert } = useStandardAlertStore();
  if (!activeSystemAlert) return null;

  const handleTypeChange = (value: SystemAlertType) => {
    setActiveSystemAlert({ ...activeSystemAlert, type: value });
  };

  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setActiveSystemAlert({ ...activeSystemAlert, message: e.target.value });
  };

  const handleScheduledStartAtChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setActiveSystemAlert({ ...activeSystemAlert, scheduledStartAt: e.target.value });
  };

  const handleScheduledEndAtChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setActiveSystemAlert({ ...activeSystemAlert, scheduledEndAt: e.target.value });
  };

  const handleCreate = () => {
    if (
      !activeSystemAlert.type ||
      !activeSystemAlert.message || activeSystemAlert.message.trim().length === 0 ||
      !activeSystemAlert.scheduledStartAt ||
      !activeSystemAlert.scheduledEndAt
    ) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('MISSING_REQUIRED_FIELD'),
      });
      return;
    }

    // Check if scheduledStartAt is before scheduledEndAt
    if (new Date(activeSystemAlert.scheduledStartAt) >= new Date(activeSystemAlert.scheduledEndAt)) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('SCHEDULED_START_BEFORE_END'),
      });
      return;
    }
    
    createSystemAlert({
      type: activeSystemAlert.type,
      message: activeSystemAlert.message,
      scheduledStartAt: localInputToIsoInstant(activeSystemAlert.scheduledStartAt),
      scheduledEndAt: localInputToIsoInstant(activeSystemAlert.scheduledEndAt),
    });
  };

  const handleEndNow = () => {
    endSystemAlert(activeSystemAlert.id);
  };
  const handleDelete = () => {
    deleteSystemAlert({ systemAlertId: activeSystemAlert.id });
  };

  return (
    <SystemAlertViewComp>
      <SystemAlertContainer>
        <FieldContainer>
          <FieldLabel>Type</FieldLabel>
          {activeSystemAlert.id ?
            <Info>{activeSystemAlert.type}</Info>
          :(
          <SelectBoxWrapper>
          <SelectBox
            value={activeSystemAlert.type}
            selectType={SelectType.SYSTEM_ALERT_TYPE}
            handleChange={handleTypeChange}
            direction={false} />
          </SelectBoxWrapper>
          )}
        </FieldContainer>
        <FieldContainer>
          <FieldLabel>Message</FieldLabel>
          {activeSystemAlert.id ?
            <Info>{activeSystemAlert.message}</Info>
          :(
          <OrgDescriptionAdmin 
            value={activeSystemAlert.message || ""}
            handleChange={handleMessageChange}
            StyledTextarea={NormalTextArea}
            alignment={TextAlignment.LEFT}
            maxLength={SYSTEM_ALERT_MESSAGE_MAX_LENGTH}
          />
          )}
        </FieldContainer>
        
        <FieldContainer>
          <FieldLabel>Created At</FieldLabel>
          <Info>{activeSystemAlert.createdAt || "Not Created Yet"}</Info>
        </FieldContainer>
        
        <FieldContainer>
          <FieldLabel>Scheduled Start</FieldLabel>
          {activeSystemAlert.id ?
            <Info>{activeSystemAlert.scheduledStartAt || "Not Scheduled Yet"}</Info>
          :
          <HeadlessInput
            type="datetime-local"
            value={activeSystemAlert.scheduledStartAt || ""}
            handleChange={handleScheduledStartAtChange}
            placeholder="Select start date"
            StyledInput={NormalInput}
          />
          }
        </FieldContainer>
        
        <FieldContainer>
          <FieldLabel>Scheduled End</FieldLabel>
          {activeSystemAlert.id ?
            <Info>{activeSystemAlert.scheduledEndAt || "Not Scheduled Yet"}</Info>
          :
          <HeadlessInput
            type="datetime-local"
            value={activeSystemAlert.scheduledEndAt || ""}
            handleChange={handleScheduledEndAtChange}
            placeholder="Select end date"
            StyledInput={NormalInput}
          />
          }
        </FieldContainer>
        
        <FieldContainer>
          <FieldLabel>Ended At</FieldLabel>
          <Info>{activeSystemAlert.endedAt || "Not Ended Yet"}</Info>
        </FieldContainer>
      </SystemAlertContainer>
      <BtnContainer>
        {activeSystemAlert.id ?(
          <>
          {!activeSystemAlert.endedAt && (
            <HeadlessBtn
              value={"End Now"}
              handleClick={handleEndNow}
              StyledBtn={BlackFitBtnHeartbeat}
            />
          )}
          <HeadlessBtn
            value={"Delete"}
            handleClick={handleDelete}
            StyledBtn={PaperBlurFitBtn}
          />
          </>
        )
        : <HeadlessBtn
          value={"Create"}
          handleClick={handleCreate}
          StyledBtn={BlackFitBtnHeartbeat}
          />
        }
        </BtnContainer>
    </SystemAlertViewComp>
  );
};


const SystemAlertViewComp = styled.section`
  position: relative;
  width: 29vw;
  height: 100%;

  display: flex;
  flex-direction: column;

  overflow-y: scroll; /* 넘칠 경우 스크롤 생성 */
  &::-webkit-scrollbar {
    display: none;
  }
  padding-right: 2px;
  padding-bottom: 40px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_04};
`;

const SystemAlertContainer = styled.div`
  display: flex;
  flex-direction: column;

  padding-top: 8px;
  padding-bottom: 16px;

  gap: 8px;
`

const Info = styled.div`
  height: 18px;
  padding-right:4px;
  margin-bottom: 1px;
  ${({ theme }) => theme.typography.Body_04};
`;

const SelectBoxWrapper = styled.article`
  width: 100%;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_02_1};
`;

const FieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 8px;
`;

const FieldLabel = styled.label`
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_03_1};
  font-weight: 500;
`;

const BtnContainer = styled.div`
  position: absolute;
  right: 0px;
  bottom: 5vh;
  width: fit-content;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5vh;
`

export default SystemAlertViewer;